import React, { useState, ChangeEvent } from "react";
import {
  TextField,
  Button,
  Typography,
  List,
  ListItem,
  Container,
  IconButton,
  Stack,
  Grid,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import { toastError } from "../../../utils/pageUtil/toast";

interface KeyValue {
  key: string;
  value: string;
  type?: string;
  id?: string;
}

interface KeyValueManagerProps<T extends KeyValue> {
  keyValues?: T[];
  onUpdateKeyValues?: (keyValues: T[]) => void;
}

const KeyValueManager = <T extends KeyValue>({
  keyValues = [],
  onUpdateKeyValues,
}: KeyValueManagerProps<T>) => {
  const { t } = useTranslation();

  const [key, setKey] = useState<string>("");
  const [value, setValue] = useState<string>("");
  const [type, setType] = useState<string>("");
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const handleInputChange =
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (e: ChangeEvent<HTMLInputElement>) => {
      setter(e.target.value);
    };

  const handleAddOrUpdateKeyValue = () => {
    if (onUpdateKeyValues) {
      let updatedKeyValues;
      if (isEditing && editingIndex !== null) {
        updatedKeyValues = keyValues.map((kv, index) =>
          index === editingIndex ? { ...kv, key, value, type } : kv,
        );
      } else {
        const isDuplicate = keyValues.some(
          (kv) =>
            kv.key?.toLowerCase() === key.toLowerCase() &&
            kv.type?.toLowerCase() === type.toLowerCase(),
        );

        if (isDuplicate) {
          toastError(t(messages.DuplicateKeyTypeError?.()));
          return;
        }

        const newKeyValue = {
          key,
          value,
          type,
          id: new Date().toISOString(),
        } as T;
        updatedKeyValues = [...keyValues, newKeyValue];
      }
      onUpdateKeyValues(updatedKeyValues);
    }
    resetFields();
  };

  const resetFields = () => {
    setKey("");
    setValue("");
    setType("");
    setIsEditing(false);
    setEditingIndex(null);
  };

  const handleDelete = (index: number) => {
    if (!onUpdateKeyValues) return;

    onUpdateKeyValues(keyValues.filter((_, i) => i !== index));

    if (editingIndex === null) return;
    if (editingIndex === index) {
      resetFields();
    } else if (editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }
  };

  const handleEdit = (index: number) => {
    const kv = keyValues[index];
    if (!kv) return;

    setKey(kv.key ?? "");
    setValue(kv.value ?? "");
    setType(kv.type || "");
    setIsEditing(true);
    setEditingIndex(index);
  };

  return (
    <Container
      component="main"
      disableGutters
      sx={{ textAlign: "center" }}
      data-testid="keyvaluemanager-test-id"
    >
      <Stack
        direction={"row"}
        alignItems={"center"}
        columnGap={2}
        flexWrap={{ xs: "wrap", sm: "nowrap" }}
        rowGap={"15px"}
      >
        <Stack width="100%">
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <Typography variant="caption">{t(messages.Key())}</Typography>
            <Tooltip
              enterDelay={1000}
              title={t(messages.Key_Tooltip_Info())}
              arrow
            >
              <InfoIcon
                sx={{ fontSize: 16, color: "#9ca3afab", cursor: "help" }}
              />
            </Tooltip>
          </Stack>
          <TextField
            fullWidth
            variant="outlined"
            onChange={handleInputChange(setKey)}
            value={key}
            id="key-input"
            sx={{
              "& label.Mui-focused": {
                color: "#ff8038",
              },
              "& .MuiFormLabel-root ": {
                top: "-7px",
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#ff8038",
                },
                "&:hover fieldset": {
                  borderColor: "#ff8038",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#ff8038",
                },
                "& input": {
                  padding: "10px",
                },
              },
            }}
          />
        </Stack>

        <Stack width="100%">
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <Typography variant="caption">{t(messages.Value())}</Typography>
            <Tooltip
              enterDelay={1000}
              title={t(messages.Value_Tooltip_Info())}
              arrow
            >
              <InfoIcon
                sx={{ fontSize: 16, color: "#9ca3afab", cursor: "help" }}
              />
            </Tooltip>
          </Stack>
          <TextField
            fullWidth
            variant="outlined"
            onChange={handleInputChange(setValue)}
            value={value}
            id="value-input"
            sx={{
              "& label.Mui-focused": {
                color: "#ff8038",
              },
              "& .MuiFormLabel-root ": {
                top: "-7px",
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#ff8038",
                },
                "&:hover fieldset": {
                  borderColor: "#ff8038",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#ff8038",
                },
                "& input": {
                  padding: "10px",
                },
              },
            }}
          />
        </Stack>

        <Stack width="100%">
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <Typography variant="caption">{t(messages.Type())}</Typography>
            <Tooltip
              enterDelay={1000}
              title={t(messages.Type_Tooltip_Info())}
              arrow
            >
              <InfoIcon
                sx={{ fontSize: 16, color: "#9ca3afab", cursor: "help" }}
              />
            </Tooltip>
          </Stack>
          <TextField
            fullWidth
            variant="outlined"
            onChange={handleInputChange(setType)}
            value={type}
            id="type-input"
            sx={{
              "& label.Mui-focused": {
                color: "#ff8038",
              },
              "& .MuiFormLabel-root ": {
                top: "-7px",
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#ff8038",
                },
                "&:hover fieldset": {
                  borderColor: "#ff8038",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#ff8038",
                },
                "& input": {
                  padding: "10px",
                },
              },
            }}
          />
        </Stack>

        <Button
          variant={"contained"}
          onClick={handleAddOrUpdateKeyValue}
          disabled={!key || !value}
          sx={{
            textTransform: "capitalize",
            height: "42px",
            bgcolor: "#ff8038",
            marginTop: "24px",
            "&:hover": {
              bgcolor: "#ff8038",
            },
          }}
        >
          {isEditing ? t(messages.Update()) : t(messages.Add())}
        </Button>
      </Stack>
      <List
        sx={{ mt: 2, maxHeight: "240px", overflowY: "auto", borderRadius: 2 }}
      >
        <ListItem
          divider
          sx={{
            display: "flex",
            justifyContent: "space-between",
            bgcolor: "background.paper",
            pt: "2px",
            pb: "2px",
            boxShadow: 0,
            borderBottom: 0,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography sx={{ flexGrow: 1, textTransform: "capitalize" }}>
                {t(messages.KeyInfo())}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography sx={{ flexGrow: 1, textTransform: "capitalize" }}>
                {t(messages.ValueInfo())}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography sx={{ flexGrow: 1, textTransform: "capitalize" }}>
                {t(messages.TypeInfo())}
              </Typography>
            </Grid>
          </Grid>
          <Typography
            sx={{
              flexGrow: 1,
              textTransform: "capitalize",
              width: "90px",
              textAlign: "center",
            }}
          >
            {t(messages.Actions())}
          </Typography>
        </ListItem>
        {keyValues?.map((kv, index) => (
          <ListItem
            key={`${kv.id ?? "row"}-${index}`}
            divider
            sx={{
              display: "flex",
              justifyContent: "space-between",
              bgcolor: index % 2 === 0 ? "#e0e0e0b5" : "background.paper",
              pt: "2px",
              pb: "2px",
              boxShadow: 0,
              borderBottom: 0,
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography sx={{ flexGrow: 1 }}>{kv.key}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography sx={{ flexGrow: 1 }}>{kv.value}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography sx={{ flexGrow: 1 }}>{kv.type}</Typography>
              </Grid>
            </Grid>

            <Stack direction={"row"}>
              <IconButton
                onClick={() => handleEdit(index)}
                color="primary"
                sx={{ color: "#ff8038" }}
              >
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => handleDelete(index)} color="error">
                <DeleteIcon />
              </IconButton>
            </Stack>
          </ListItem>
        ))}
      </List>
    </Container>
  );
};

export default KeyValueManager;
